'use client';

/**
 * QASkeleton — Reception answer-card loading placeholder.
 *
 * Step 2 of the answer-load choreography: while the fast tier is in flight
 * (`useDocsStore.isLoading`), the answer/telemetry cards paint this instead of
 * returning null — so the reception shows the answer's *shape forming* rather
 * than a blank pane that pops. Reuses the same QA_SECTION + QA_BOX shell as the
 * real cards, so when content lands it swaps in with no layout jump.
 *
 * Generic shimmer bars — no card-kind hinting (a wrong kind-guess reads as
 * broken; the sized box already kills the reflow). bg-muted (no invented
 * colour), reduced-motion safe, role=status for a11y.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { QA_SECTION, QA_BOX } from './web-qa-chrome';

const WIDTHS = ['w-full', 'w-[92%]', 'w-[68%]', 'w-[84%]', 'w-1/2'];

export function QASkeleton({ label, lines = 3 }: { label: string; lines?: number }) {
  const T = useCardTypography();
  return (
    <section className={QA_SECTION} role="status" aria-label={`${label} loading`}>
      <div className="w-full">
        <div className={QA_BOX}>
          <div className={cn(T.metadata, 'uppercase tracking-wider text-xs')}>{label}</div>
          <div className="flex flex-col gap-2">
            {Array.from({ length: lines }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'h-3 rounded bg-muted animate-pulse motion-reduce:animate-none',
                  WIDTHS[i % WIDTHS.length],
                )}
              />
            ))}
          </div>
          <span className="sr-only">Loading…</span>
        </div>
      </div>
    </section>
  );
}
